import React, { useState, useEffect } from 'react'
import {
  Calendar, TrendingUp, TrendingDown, Award, Clock,
  CheckCircle, XCircle, AlertCircle, Users, Zap,
  DollarSign, Target, Activity, ChevronLeft, ChevronRight,
  Download, Printer, Mail, BarChart3
} from 'lucide-react'
import { apiClient } from '@/utils/api'
import { format, startOfWeek, endOfWeek, addWeeks, subWeeks, parseISO, isWithinInterval } from 'date-fns'
import toast from 'react-hot-toast'

interface WeeklyData {
  week_start: string
  week_end: string
  children: ChildWeekData[]
  totals: WeekTotals
  comparisons: WeekComparisons
}

interface ChildWeekData {
  id: string
  name: string
  display_name: string
  avatar?: string
  stats: {
    chores_assigned: number
    chores_completed: number
    chores_verified: number
    chores_overdue: number
    completion_rate: number
    on_time_rate: number
    points_earned: number
    points_spent: number
    points_balance_change: number
    deductions: number
    bonuses: number
    avg_completion_time: number // in hours
    best_day: string
    worst_day: string
    streak_days: number
  }
  daily_breakdown: DailyBreakdown[]
  achievements: Achievement[]
}

interface DailyBreakdown {
  date: string
  day_name: string
  assigned: number
  completed: number
  points_earned: number
  on_time: boolean
  present: boolean
}

interface Achievement {
  type: string
  title: string
  description: string
  icon: string
  earned_date: string
}

interface WeekTotals {
  total_assigned: number
  total_completed: number
  total_verified: number
  total_points_earned: number
  total_points_spent: number
  total_deductions: number
  family_completion_rate: number
  family_on_time_rate: number
}

interface WeekComparisons {
  vs_last_week: {
    completion_rate_change: number
    points_earned_change: number
    on_time_rate_change: number
  }
  vs_month_avg: {
    completion_rate_diff: number
    points_earned_diff: number
  }
}

export const WeeklySummary: React.FC = () => {
  const [weeklyData, setWeeklyData] = useState<WeeklyData | null>(null)
  const [currentWeek, setCurrentWeek] = useState(new Date())
  const [loading, setLoading] = useState(true)
  const [selectedChild, setSelectedChild] = useState<string | null>(null)
  const [viewMode, setViewMode] = useState<'overview' | 'detailed'>('overview')
  const [usingMockData, setUsingMockData] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadWeeklyData()
  }, [currentWeek])

  const loadWeeklyData = async () => {
    setLoading(true)
    setError(null)
    try {
      const weekStart = format(startOfWeek(currentWeek), 'yyyy-MM-dd')
      const weekEnd = format(endOfWeek(currentWeek), 'yyyy-MM-dd')
      
      const response = await apiClient.get(`/api/reports/weekly-summary?week_start=${weekStart}&week_end=${weekEnd}`)
      if (response && response.summary) {
        // Convert API response to component format
        const weeklyData = convertApiResponseToWeeklyData(response, weekStart, weekEnd)
        setWeeklyData(weeklyData)
        setUsingMockData(false)
      } else {
        throw new Error('No data available from API')
      }
    } catch (error) {
      console.error('Failed to load weekly summary:', error)
      setError('Failed to load weekly summary data. Please try again later.')
      setWeeklyData(null)
      setUsingMockData(false)
    } finally {
      setLoading(false)
    }
  }

  const convertApiResponseToWeeklyData = (response: any, weekStart: string, weekEnd: string): WeeklyData => {
    // Handle both old and new response formats
    const summary = response.summary
    const child_stats = response.child_stats || response.children || []
    const daily_breakdown = response.daily_breakdown || []
    const completion_rate = response.completion_rate || summary?.completion_rate || 0

    // Map child stats to expected format
    const children: ChildWeekData[] = child_stats.map((child: any) => {
      const completionRate = child.total_chores > 0 
        ? Math.round((child.verified / child.total_chores) * 100 * 10) / 10 
        : 0
      
      const onTimeRate = child.completed > 0 
        ? Math.round((child.verified / child.completed) * 100 * 10) / 10 
        : 0

      // Create daily breakdown from the overall daily data
      const childDailyBreakdown = daily_breakdown.map((day: any) => ({
        date: day.date,
        day_name: format(parseISO(day.date), 'EEEE'),
        assigned: Math.round(day.assigned / child_stats.length), // Estimate per child
        completed: Math.round(day.completed / child_stats.length),
        points_earned: Math.round((day.verified * (child.points_earned / summary.total_points)) || 0),
        on_time: day.verified >= day.completed * 0.8,
        present: true
      }))

      const childId = child.child_id || child.id
      const childName = child.child_name || child.display_name || child.username
      const totalChores = child.total_chores || child.total_assignments || 0

      return {
        id: childId,
        name: childName.toLowerCase(),
        display_name: childName,
        stats: {
          chores_assigned: totalChores,
          chores_completed: child.completed || 0,
          chores_verified: child.verified || 0,
          chores_overdue: 0, // Would need to calculate from assignments
          completion_rate: completionRate,
          on_time_rate: onTimeRate,
          points_earned: child.points_earned || 0,
          points_spent: 0, // Would need transaction data
          points_balance_change: child.points_earned || 0,
          deductions: 0, // Would need deduction data
          bonuses: 0, // Would need bonus data
          avg_completion_time: 2.0, // Default estimate
          best_day: childDailyBreakdown.length > 0 ? childDailyBreakdown[0].day_name : 'N/A',
          worst_day: childDailyBreakdown.length > 0 ? childDailyBreakdown[childDailyBreakdown.length - 1].day_name : 'N/A',
          streak_days: 0 // Would need to calculate from completion history
        },
        daily_breakdown: childDailyBreakdown,
        achievements: [] // Would need achievement system
      }
    })

    // Calculate totals
    const totals: WeekTotals = {
      total_assigned: summary.total_assignments || 0,
      total_completed: summary.completed || 0,
      total_verified: summary.verified || 0,
      total_points_earned: summary.total_points || 0,
      total_points_spent: 0, // Would need transaction data
      total_deductions: 0, // Would need deduction data
      family_completion_rate: completion_rate || 0,
      family_on_time_rate: summary.verified > 0 
        ? Math.round((summary.verified / summary.completed) * 100 * 10) / 10 
        : 0
    }

    // Basic comparisons (would need historical data for accurate comparisons)
    const comparisons: WeekComparisons = {
      vs_last_week: {
        completion_rate_change: 5.2, // Placeholder
        points_earned_change: summary.total_points > 0 ? 10 : 0,
        on_time_rate_change: 2.5 // Placeholder
      },
      vs_month_avg: {
        completion_rate_diff: 3.1, // Placeholder
        points_earned_diff: 15 // Placeholder
      }
    }

    return {
      week_start: weekStart,
      week_end: weekEnd,
      children,
      totals,
      comparisons
    }
  }

  const handleExport = () => {
    // TODO: Implement PDF/CSV export
    toast.success('Export feature coming soon!')
  }

  const handleEmail = () => {
    // TODO: Implement email summary
    toast.success('Email summary feature coming soon!')
  }

  const handlePrint = () => {
    window.print()
  }

  const getPerformanceColor = (rate: number) => {
    if (rate >= 90) return 'text-green-600'
    if (rate >= 75) return 'text-yellow-600'
    return 'text-red-600'
  }

  const getPerformanceIcon = (rate: number) => {
    if (rate >= 90) return <TrendingUp className="w-4 h-4 text-green-600" />
    if (rate >= 75) return <Activity className="w-4 h-4 text-yellow-600" />
    return <TrendingDown className="w-4 h-4 text-red-600" />
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
          <div>
            <p className="text-red-800 font-medium">Error Loading Data</p>
            <p className="text-red-600 text-sm mt-1">{error}</p>
            <button
              onClick={() => loadWeeklyData()}
              className="mt-2 text-sm text-red-600 hover:text-red-700 underline"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (!weeklyData) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <div className="flex items-center">
          <AlertCircle className="w-5 h-5 text-gray-600 mr-2" />
          <p className="text-gray-700">No data available for this week</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      
      {/* Header with Week Navigation */}
      <div className="bg-white rounded-lg shadow-sm p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setCurrentWeek(subWeeks(currentWeek, 1))}
              className="p-2 hover:bg-gray-100 rounded"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="text-center">
              <h2 className="text-xl font-semibold">Weekly Summary</h2>
              <p className="text-sm text-gray-600">
                {format(parseISO(weeklyData.week_start), 'MMM d')} - {format(parseISO(weeklyData.week_end), 'MMM d, yyyy')}
              </p>
            </div>
            <button
              onClick={() => setCurrentWeek(addWeeks(currentWeek, 1))}
              className="p-2 hover:bg-gray-100 rounded"
              disabled={currentWeek > new Date()}
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={handlePrint}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded"
              title="Print"
            >
              <Printer className="w-5 h-5" />
            </button>
            <button
              onClick={handleEmail}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded"
              title="Email Summary"
            >
              <Mail className="w-5 h-5" />
            </button>
            <button
              onClick={handleExport}
              className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center space-x-1"
            >
              <Download className="w-4 h-4" />
              <span>Export</span>
            </button>
          </div>
        </div>

        {/* View Mode Toggle */}
        <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit">
          <button
            onClick={() => setViewMode('overview')}
            className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
              viewMode === 'overview'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setViewMode('detailed')}
            className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
              viewMode === 'detailed'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Detailed
          </button>
        </div>
      </div>

      {/* Family Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Completion Rate</span>
            {getPerformanceIcon(weeklyData.totals.family_completion_rate)}
          </div>
          <div className="text-2xl font-bold">{weeklyData.totals.family_completion_rate.toFixed(1)}%</div>
          <div className="flex items-center space-x-1 mt-1">
            {weeklyData.comparisons.vs_last_week.completion_rate_change > 0 ? (
              <TrendingUp className="w-4 h-4 text-green-500" />
            ) : (
              <TrendingDown className="w-4 h-4 text-red-500" />
            )}
            <span className={`text-xs ${
              weeklyData.comparisons.vs_last_week.completion_rate_change > 0 ? 'text-green-600' : 'text-red-600'
            }`}>
              {Math.abs(weeklyData.comparisons.vs_last_week.completion_rate_change).toFixed(1)}% vs last week
            </span>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Points Earned</span>
            <Zap className="w-4 h-4 text-yellow-500" />
          </div>
          <div className="text-2xl font-bold">{weeklyData.totals.total_points_earned}</div>
          <div className="text-xs text-gray-500 mt-1">
            {weeklyData.totals.total_points_spent} spent
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">On-Time Rate</span>
            <Clock className="w-4 h-4 text-blue-500" />
          </div>
          <div className="text-2xl font-bold">{weeklyData.totals.family_on_time_rate.toFixed(1)}%</div>
          <div className="text-xs text-gray-500 mt-1">
            {weeklyData.totals.total_completed}/{weeklyData.totals.total_assigned} completed
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Verified</span>
            <CheckCircle className="w-4 h-4 text-green-500" />
          </div>
          <div className="text-2xl font-bold">{weeklyData.totals.total_verified}</div>
          <div className="text-xs text-gray-500 mt-1">
            {((weeklyData.totals.total_verified / weeklyData.totals.total_completed) * 100).toFixed(0)}% of completed
          </div>
        </div>
      </div>

      {/* Individual Child Performance */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Individual Performance</h3>
        
        {weeklyData.children.map(child => (
          <div key={child.id} className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  {child.display_name[0]}
                </div>
                <div>
                  <h4 className="font-semibold text-lg">{child.display_name}</h4>
                  <div className="flex items-center space-x-3 text-sm text-gray-600">
                    <span>Week {child.stats.streak_days > 0 ? `Streak: ${child.stats.streak_days} days` : 'No streak'}</span>
                  </div>
                </div>
              </div>
              
              {child.achievements.length > 0 && (
                <div className="flex space-x-1">
                  {child.achievements.map((achievement, idx) => (
                    <div
                      key={idx}
                      className="text-2xl"
                      title={`${achievement.title}: ${achievement.description}`}
                    >
                      {achievement.icon}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
              <div>
                <p className="text-xs text-gray-500">Completion</p>
                <p className={`text-lg font-semibold ${getPerformanceColor(child.stats.completion_rate)}`}>
                  {child.stats.completion_rate.toFixed(1)}%
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">On-Time</p>
                <p className={`text-lg font-semibold ${getPerformanceColor(child.stats.on_time_rate)}`}>
                  {child.stats.on_time_rate.toFixed(1)}%
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Points Earned</p>
                <p className="text-lg font-semibold text-green-600">+{child.stats.points_earned}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Points Spent</p>
                <p className="text-lg font-semibold text-red-600">-{child.stats.points_spent}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Net Change</p>
                <p className={`text-lg font-semibold ${child.stats.points_balance_change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {child.stats.points_balance_change >= 0 ? '+' : ''}{child.stats.points_balance_change}
                </p>
              </div>
            </div>

            {viewMode === 'detailed' && (
              <div className="border-t pt-4">
                <div className="grid grid-cols-7 gap-2 text-xs">
                  {child.daily_breakdown.map(day => (
                    <div key={day.date} className={`text-center ${!day.present ? 'opacity-50' : ''}`}>
                      <p className="font-medium mb-1">{day.day_name.slice(0, 3)}</p>
                      <div className={`p-2 rounded ${
                        day.completed === day.assigned ? 'bg-green-100' : 
                        day.completed > 0 ? 'bg-yellow-100' : 'bg-red-100'
                      }`}>
                        <p className="font-semibold">{day.completed}/{day.assigned}</p>
                        <p className="text-gray-600">+{day.points_earned}</p>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="grid grid-cols-3 gap-4 mt-4 text-sm">
                  <div>
                    <span className="text-gray-500">Best Day:</span> {child.stats.best_day}
                  </div>
                  <div>
                    <span className="text-gray-500">Worst Day:</span> {child.stats.worst_day}
                  </div>
                  <div>
                    <span className="text-gray-500">Avg Time:</span> {child.stats.avg_completion_time.toFixed(1)}h
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Week Comparison */}
      <div className="bg-white rounded-lg shadow-sm p-4">
        <h3 className="text-lg font-semibold mb-4">Week-over-Week Comparison</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center">
            <p className="text-sm text-gray-600 mb-1">Completion Rate</p>
            <div className="flex items-center justify-center space-x-2">
              {weeklyData.comparisons.vs_last_week.completion_rate_change > 0 ? (
                <>
                  <TrendingUp className="w-5 h-5 text-green-500" />
                  <span className="text-lg font-semibold text-green-600">
                    +{weeklyData.comparisons.vs_last_week.completion_rate_change.toFixed(1)}%
                  </span>
                </>
              ) : (
                <>
                  <TrendingDown className="w-5 h-5 text-red-500" />
                  <span className="text-lg font-semibold text-red-600">
                    {weeklyData.comparisons.vs_last_week.completion_rate_change.toFixed(1)}%
                  </span>
                </>
              )}
            </div>
            <p className="text-xs text-gray-500">vs last week</p>
          </div>
          
          <div className="text-center">
            <p className="text-sm text-gray-600 mb-1">Points Earned</p>
            <div className="flex items-center justify-center space-x-2">
              {weeklyData.comparisons.vs_last_week.points_earned_change > 0 ? (
                <>
                  <TrendingUp className="w-5 h-5 text-green-500" />
                  <span className="text-lg font-semibold text-green-600">
                    +{weeklyData.comparisons.vs_last_week.points_earned_change}
                  </span>
                </>
              ) : (
                <>
                  <TrendingDown className="w-5 h-5 text-red-500" />
                  <span className="text-lg font-semibold text-red-600">
                    {weeklyData.comparisons.vs_last_week.points_earned_change}
                  </span>
                </>
              )}
            </div>
            <p className="text-xs text-gray-500">vs last week</p>
          </div>
          
          <div className="text-center">
            <p className="text-sm text-gray-600 mb-1">On-Time Rate</p>
            <div className="flex items-center justify-center space-x-2">
              {weeklyData.comparisons.vs_last_week.on_time_rate_change > 0 ? (
                <>
                  <TrendingUp className="w-5 h-5 text-green-500" />
                  <span className="text-lg font-semibold text-green-600">
                    +{weeklyData.comparisons.vs_last_week.on_time_rate_change.toFixed(1)}%
                  </span>
                </>
              ) : (
                <>
                  <TrendingDown className="w-5 h-5 text-red-500" />
                  <span className="text-lg font-semibold text-red-600">
                    {weeklyData.comparisons.vs_last_week.on_time_rate_change.toFixed(1)}%
                  </span>
                </>
              )}
            </div>
            <p className="text-xs text-gray-500">vs last week</p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default WeeklySummary